import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useVideoStatsStore, VideoStat } from '../../stores/videoStatsStore'
import { useUserStore } from '../../stores/userStore'
import { formatDuration } from '../../utils/formatDuration'

function VideoRow({ stat }: { stat: VideoStat }) {
  const [open, setOpen] = useState(false)
  const { watchers, fetchVideoWatchers } = useVideoStatsStore()
  const { users } = useUserStore()

  const handleExpand = async () => {
    if (!open) await fetchVideoWatchers(stat.videoId)
    setOpen((v) => !v)
  }

  const rowWatchers = watchers[stat.videoId] ?? []
  const background = Math.min(stat.totalBackgroundSeconds, stat.totalWatchedSeconds)
  const foreground = stat.totalWatchedSeconds - background

  const getUserName = (userId: string) => {
    const u = users.find((u) => u.id === userId)
    return u?.displayName ?? userId
  }

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={handleExpand}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {stat.title || stat.videoId}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {stat.videoId}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip label={`${stat.totalPlays} 次`} size="small" color="primary" />
        </TableCell>
        <TableCell align="right">{formatDuration(stat.totalWatchedSeconds)}</TableCell>
        <TableCell align="right">{formatDuration(foreground)}</TableCell>
        <TableCell align="right">
          {background > 0 ? formatDuration(background) : '-'}
        </TableCell>
        <TableCell align="right">
          {stat.totalPlays > 0
            ? formatDuration(Math.round(stat.totalWatchedSeconds / stat.totalPlays))
            : '-'}
        </TableCell>
        <TableCell align="right">{stat.totalPauseCount}</TableCell>
        <TableCell align="right">{stat.totalFullscreenCount}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, borderBottom: 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                觀看者清單（{rowWatchers.length} 人）
              </Typography>
              {rowWatchers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  無資料
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>使用者</TableCell>
                      <TableCell align="right">播放次數</TableCell>
                      <TableCell align="right">累計觀看時長</TableCell>
                      <TableCell align="right">最後觀看</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowWatchers.map((w) => (
                      <TableRow key={w.userId}>
                        <TableCell>{getUserName(w.userId)}</TableCell>
                        <TableCell align="right">{w.plays}</TableCell>
                        <TableCell align="right">{formatDuration(w.totalSeconds)}</TableCell>
                        <TableCell align="right">
                          {w.lastWatchedAt
                            ? w.lastWatchedAt.toLocaleString('zh-TW')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function VideoStatsPanel() {
  const { videoStats, loading, error, fetchVideoStats } = useVideoStatsStore()
  const { fetchUsers } = useUserStore()

  useEffect(() => {
    fetchVideoStats()
    fetchUsers()
  }, [fetchVideoStats, fetchUsers])

  if (loading && videoStats.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (videoStats.length === 0) {
    return <Alert severity="info">目前尚無影片觀看資料。</Alert>
  }

  return (
    <Card>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell padding="checkbox" />
            <TableCell sx={{ fontWeight: 600 }}>影片</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>播放次數</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>總觀看時長</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>頁面內觀看</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>背景續播</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>平均觀看時長</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>暫停次數</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>全螢幕次數</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {videoStats.map((stat) => (
            <VideoRow key={stat.videoId} stat={stat} />
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}
